/* eslint-disable */
import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark, faExclamationTriangle, faCheckCircle, faClock, faExternalLinkAlt, faTimes, faSort, faSortUp, faSortDown, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { client } from "../../../../services/axiosClient.js";
import { ThreeDots } from "react-loader-spinner";
import AuthContext from "contexts/AuthProvider.js";
import { Line } from "rc-progress";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import Button from "components/CustomButtons/Button";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";

// ================== Constantes para Status das Ações ==================
// Estas constantes devem corresponder aos valores traduzidos do arquivo de idioma
const ACTION_STATUS = {
  ON_HOLD: "Em Espera",
  IN_PROGRESS: "Em Progresso", 
  DONE: "Concluído"
};

// Função para calcular o progresso de um plano específico
function calculatePlanProgress(risk, planType) {
  if (planType === "mitigation" && !risk.hasMitigationPlan) return 100;
  if (planType === "contingency" && !risk.hasContingencyPlan) return 100;

  let totalActions = 0;
  let doneActions = 0;
  if (!risk.rootCauses || !Array.isArray(risk.rootCauses)) return 100;

  risk.rootCauses.forEach((rc) => {
    if (rc.actions && Array.isArray(rc.actions)) {
      const filtered = rc.actions.filter((act) => act.type === planType);
      totalActions += filtered.length;
      filtered.forEach((act) => {
        if (act.status === ACTION_STATUS.DONE) doneActions += 1;
      });
    }
  });

  if (totalActions === 0) return 100;
  return Math.round((doneActions / totalActions) * 100);
}

// Função para obter a cor do progresso
function getProgressColor(percentage) {
  if (percentage >= 100) {
    return "#4CAF50"; // Green
  } else if (percentage >= 50) {
    return "#FFC107"; // Yellow
  } else {
    return "#F44336"; // Red
  }
}

// Função para obter o estilo do círculo de status
// Usa as mesmas cores da matriz de riscos do RiskManagement.js
const getStatusCircleStyle = (risk_level) => {
  let background, borderColor;
  switch (risk_level) {
    case "Red":
      background = "#FFEBEE"; // light red
      borderColor = "#E57373";
      break;
    case "Yellow":
      background = "#FFF9C4"; // light yellow
      borderColor = "#FFB74D";
      break;
    case "Green":
      background = "#C8E6C9"; // light green
      borderColor = "#81C784";
      break;
    default:
      background = "#E0E0E0";
      borderColor = "#9E9E9E";
  }
  return {
    background,
    border: `2px solid ${borderColor}`,
    borderRadius: "50%",
    width: "35px",
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  };
};

// Função para calcular estatísticas de ações
function calculateActionStats(risk, planType) {
  if (planType === "mitigation" && !risk.hasMitigationPlan) return { total: 0, completed: 0, expectedToday: 0 };
  if (planType === "contingency" && !risk.hasContingencyPlan) return { total: 0, completed: 0, expectedToday: 0 };

  let totalActions = 0;
  let completedActions = 0;
  let expectedTodayActions = 0;
  const today = new Date().toISOString().split('T')[0];

  if (risk.rootCauses && Array.isArray(risk.rootCauses)) {
    risk.rootCauses.forEach((rc) => {
      if (rc.actions && Array.isArray(rc.actions)) {
        const filtered = rc.actions.filter((act) => act.type === planType);
        totalActions += filtered.length;
        
        filtered.forEach((act) => {
          if (act.status === ACTION_STATUS.DONE) {
            completedActions += 1;
          } else if (act.dueDate && act.dueDate <= today) {
            expectedTodayActions += 1;
          }
        });
      }
    });
  }

  return { total: totalActions, completed: completedActions, expectedToday: expectedTodayActions };
}

// Componente da tabela de riscos
const RiskTable = ({ risks, branchOptions, userOptions, clientColor, t }) => {
  const [filters, setFilters] = useState({
    branch: "",
    risk_level: "",
    probability: "",
    impact: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data, config) => {
    if (!config.key) return data;
    const { key, direction } = config;
    return [...data].sort((a, b) => {
      const aVal = (a[key] || "").toString().toLowerCase();
      const bVal = (b[key] || "").toString().toLowerCase();
      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const filteredRisks = risks.filter((r) => {
    const branchOk = !filters.branch || r.branch === filters.branch;
    const levelOk = !filters.risk_level || r.risk_level === filters.risk_level;
    const probOk = !filters.probability || r.probability === filters.probability;
    const impOk = !filters.impact || r.impact === filters.impact;
    return branchOk && levelOk && probOk && impOk;
  });

  const finalList = sortData(filteredRisks, sortConfig);

  // Estilos seguindo o padrão do RiskManagement.js
  const useStyles = makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    filterSection: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      padding: "0 16px",
      position: "relative",
      marginBottom: "16px",
      gap: "8px", // Adicionar gap para espaçamento consistente
    },
    clearFiltersWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      paddingTop: "5.2px", // Mesmo padding dos filtros
    },
    clearFiltersIcon: {
      position: "absolute",
      top: "2px",
      right: "2px",
      backgroundColor: "transparent",
      color: "#666",
      fontSize: "8px",
      fontWeight: "bold",
      borderRadius: "50%",
      width: 8,
      height: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    selectFormControlOverride: {
      margin: "3px 0 5px 0 !important",
      paddingTop: "1px",
      paddingBottom: "1px",
      "& > div": {
        "&:before": {
          borderBottomWidth: "0.8px !important",
        },
      },
    },
    tableHeadCell: {
      fontWeight: "bold",
      backgroundColor: "white",
      cursor: "pointer",
      padding: "12px 8px",
      fontSize: "0.875rem",
    },
    sortIcon: {
      fontSize: "14px",
      marginLeft: "6px",
      color: "#ccc",
    },
    activeSortIcon: {
      color: "#aaa",
    },
    tableContainer: {
      maxHeight: "400px",
      overflow: "auto",
    },
    tableRow: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
    tableCell: {
      padding: "12px 8px",
      fontSize: "0.875rem",
      borderBottom: "1px solid #e0e0e0",
    },
    // Versão reduzida do ActionStatCard para a tabela
    compactActionStats: {
      display: "flex",
      alignItems: "center",
      padding: "6px 8px",
      backgroundColor: "#f8f9fa",
      borderRadius: "6px",
      border: "1px solid #e0e0e0",
      gap: "8px",
      minHeight: "36px",
      width: "100%",
      justifyContent: "center",
    },
    compactActionStatsIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "18px",
      height: "18px",
      borderRadius: "4px",
      flexShrink: 0,
    },
    compactActionStatsContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "2px",
      minWidth: 0,
    },
    compactActionStatsValue: {
      fontSize: "0.8rem",
      fontWeight: "700",
      color: "#333",
      lineHeight: 1,
    },
    compactActionStatsSubtitle: {
      fontSize: "0.65rem",
      color: "#666",
      fontWeight: "500",
      lineHeight: 1,
      textAlign: "center",
    },
    actionStatsProgress: {
      marginTop: "4px",
      width: "100%",
    },
  }));

  const classes = useStyles();

  return (
    <div>
      {/* Seção de filtros seguindo o padrão do RiskManagement.js */}
      <div className={classes.filterSection}>
        {/* Filtros usando GridContainer e GridItem como no RiskManagement.js */}
        <GridContainer spacing={0}>
          {/* Clean Filters button - agora como primeiro GridItem */}
          <GridItem xs={3} md={1}>
            <div className={classes.clearFiltersWrapper}>
                                              <Tooltip title={t("riskmanagement.clearAllFilters")}>
                  <FontAwesomeIcon
                    icon={faFilterCircleXmark}
                    style={{
                      fontSize: "16px",
                      color: "#666",
                      cursor: "pointer",
                    }}
                    onClick={() => setFilters({
                      branch: "",
                      risk_level: "",
                      probability: "",
                      impact: "",
                    })}
                  />
                </Tooltip>
            </div>
          </GridItem>

          <GridItem xs={9} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.branch}
                onChange={(e) => handleFilterChange("branch", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem
                  value=""
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {t("riskmanagement.all_branches")}
                </MenuItem>
                {branchOptions.map((opt) => (
                  <MenuItem
                    key={opt.id}
                    value={opt.name}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem xs={12} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.risk_level}
                onChange={(e) => handleFilterChange("risk_level", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">{t("riskmanagement.all_levels")}</MenuItem>
                <MenuItem
                  value="Red"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Red
                </MenuItem>
                <MenuItem
                  value="Yellow"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Yellow
                </MenuItem>
                <MenuItem
                  value="Green"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Green
                </MenuItem>
              </Select>
            </FormControl>
          </GridItem>

          <GridItem xs={12} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.probability}
                onChange={(e) => handleFilterChange("probability", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">{t("riskmanagement.all_probabilities")}</MenuItem>
                <MenuItem
                  value="Low"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Low
                </MenuItem>
                <MenuItem
                  value="Medium"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Medium
                </MenuItem>
                <MenuItem
                  value="High"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  High
                </MenuItem>
              </Select>
            </FormControl>
          </GridItem>

          <GridItem xs={12} md={3}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.impact}
                onChange={(e) => handleFilterChange("impact", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">{t("riskmanagement.all_impacts")}</MenuItem>
                <MenuItem
                  value="Low"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Low
                </MenuItem>
                <MenuItem
                  value="Medium"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  Medium
                </MenuItem>
                <MenuItem
                  value="High"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  High
                </MenuItem>
              </Select>
            </FormControl>
          </GridItem>
        </GridContainer>
      </div>

      {/* Tabela de riscos */}
      <div className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell} onClick={() => handleSort("title")}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {t("riskmanagement.tableColumns.risk")}
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "title"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "title"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell className={classes.tableHeadCell} onClick={() => handleSort("risk_level")}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {t("riskmanagement.tableColumns.riskLevel")}
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "risk_level"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "risk_level"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell className={classes.tableHeadCell} onClick={() => handleSort("owner")}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {t("riskmanagement.tableColumns.owner")}
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "owner"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "owner"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                {t("riskmanagement.tableColumns.mitigationProgress")}
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                {t("riskmanagement.tableColumns.contingencyProgress")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {finalList.map((risk) => {
              const mitStats = calculateActionStats(risk, "mitigation");
              const conStats = calculateActionStats(risk, "contingency");
              
              return (
                <TableRow key={risk.id} className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>
                    {risk.title}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div
                      style={getStatusCircleStyle(risk.risk_level)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {risk.owner}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {risk.hasMitigationPlan ? (
                      <div className={classes.compactActionStats}>
                        <div 
                          className={classes.compactActionStatsIcon}
                          style={{
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                          }}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: "10px" }} />
                        </div>
                        <div className={classes.compactActionStatsContent}>
                          <span className={classes.compactActionStatsValue}>
                            {mitStats.completed}/{mitStats.expectedToday}
                          </span>
                          <span className={classes.compactActionStatsSubtitle}>
                            {t("riskmanagement.completed_expected_today_short")}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <FontAwesomeIcon icon={faCircleXmark} />
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {risk.hasContingencyPlan ? (
                      <div className={classes.compactActionStats}>
                        <div 
                          className={classes.compactActionStatsIcon}
                          style={{
                            backgroundColor: "#fff3e0",
                            color: "#f57c00",
                          }}
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: "10px" }} />
                        </div>
                        <div className={classes.compactActionStatsContent}>
                          <span className={classes.compactActionStatsValue}>
                            {conStats.completed}/{conStats.expectedToday}
                          </span>
                          <span className={classes.compactActionStatsSubtitle}>
                            {t("riskmanagement.completed_expected_today_short")}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <FontAwesomeIcon icon={faCircleXmark} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Componente para renderizar o status do risco com design moderno
function RiskStatusChip({ level, count, total, color, t }) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "row", // Mudança para horizontal para melhor uso do espaço
        alignItems: "center",
        padding: "6px 8px",
        backgroundColor: "#f8f9fa",
        borderRadius: "6px",
        border: `2px solid ${color}20`,
        minHeight: "42px",
        position: "relative",
        overflow: "hidden",
        gap: "10px",
      }}
    >
      {/* Indicador de cor no lado esquerdo */}
      <Box
        style={{
          width: "3px",
          height: "100%",
          backgroundColor: color,
          borderRadius: "2px",
          position: "absolute",
          left: 0,
          top: 0,
        }}
      />

      {/* Contador principal */}
      <Typography
        variant="h4"
        style={{
          fontSize: "1.1rem",
          fontWeight: "700",
          color: color,
          lineHeight: 1,
          minWidth: "25px",
          textAlign: "center",
        }}
      >
        {count}
      </Typography>

      {/* Informações do lado direito */}
      <Box style={{ flex: 1, display: "flex", flexDirection: "column", gap: "1px" }}>
        {/* Label do nível */}
        <Typography
          variant="body2"
          style={{
            fontSize: "0.65rem",
            fontWeight: "600",
            color: "#666",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            lineHeight: 1,
          }}
        >
          {level}
        </Typography>

        {/* Percentual */}
        <Typography
          variant="caption"
          style={{
            fontSize: "0.55rem",
            color: "#999",
            fontWeight: "500",
            lineHeight: 1,
          }}
        >
          {percentage}{t("riskmanagement.percentage_of_total")}
        </Typography>
      </Box>
    </Box>
  );
}

// Componente para estatísticas de ações
function ActionStatCard({ icon, title, value, color, subtitle }) {
  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 8px",
        backgroundColor: "#f8f9fa",
        borderRadius: "6px",
        border: `1px solid ${color}20`,
        gap: "8px",
        minHeight: "42px",
      }}
    >
      {/* Ícone */}
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "20px",
          height: "20px",
          borderRadius: "4px",
          backgroundColor: `${color}15`,
          color: color,
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: "8px" }} />
      </Box>

      {/* Conteúdo */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          style={{
            fontSize: "0.8rem",
            fontWeight: "700",
            color: color,
            marginBottom: "1px",
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          style={{
            fontSize: "0.55rem",
            color: "#666",
            fontWeight: "500",
            lineHeight: 1,
            marginBottom: "1px",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            style={{
              fontSize: "0.45rem",
              color: "#999",
              lineHeight: 1,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Componente principal
const RiskSummary = ({ clientColor, isLoading: externalIsLoading, isModalOpen, onCloseModal }) => {
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const [riskData, setRiskData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Usar loading externo se fornecido, caso contrário usar interno
  const shouldShowLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  // Estilos
  const useStyles = makeStyles((theme) => ({
    root: {
      padding: "6px 8px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    section: {
      marginBottom: "8px",
    },
    sectionTitle: {
      fontSize: "0.7rem",
      fontWeight: "600",
      marginBottom: "6px",
      color: "#555",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    // Novo layout de duas colunas
    twoColumnLayout: {
      display: "flex",
      gap: "12px",
      marginBottom: "8px",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        gap: "8px",
      },
    },
    leftColumn: {
      flex: "1",
      minWidth: 0, // Permite que a coluna encolha se necessário
    },
    rightColumn: {
      flex: "1",
      minWidth: 0, // Permite que a coluna encolha se necessário
    },
    // Estilos para contadores verticais
    verticalRiskCounters: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    // Estilos para ações verticais
    verticalActionCards: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    summaryBox: {
      backgroundColor: "#f8f9fa",
      padding: "8px",
      borderRadius: "8px",
      border: `1px solid ${clientColor}20`,
      textAlign: "center",
      marginTop: "auto",
    },
    noDataMessage: {
      textAlign: "center",
      color: "#999",
      fontSize: "0.75rem",
      fontStyle: "italic",
      padding: "20px",
    },
    // Estilos para o botão do card header (seguindo padrão do WhatsHappeningV4)
    cardButton: {
      padding: "8px !important",
      margin: "0 !important",
      height: "30px !important",
      width: "30px !important",
      minWidth: "0 !important",
      position: "relative",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": {
        transform: "scale(1.05)",
      },
    },
    cardButtonIcon: {
      margin: "0 !important",
      width: "18px !important",
      height: "18px !important",
      color: clientColor,
    },
    modalTitle: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 24px",
      borderBottom: `1px solid ${clientColor}20`,
      backgroundColor: "#f8f9fa",
    },
    modalContent: {
      padding: 0,
      minHeight: "600px",
      overflow: "hidden",
    },
    closeButton: {
      color: "#999",
      "&:hover": {
        color: clientColor,
      },
    },
  }));

  const classes = useStyles();

  // Função para fechar o modal
  const handleCloseModal = () => {
    onCloseModal();
  };

  // Buscar dados dos riscos
  const fetchRiskData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Buscar dados do resumo (para o card principal)
      const summaryResponse = await client.post("/api/risk-summary-data/", {
          action: "list",
        });

      // Buscar dados completos dos riscos (para a tabela)
      const risksResponse = await client.post("/api/risk-management/", {
        action: "list_risks",
      });

      if (summaryResponse.data?.success && risksResponse.data?.success) {
        const risks = risksResponse.data.risks || [];
        
        // Calcular contadores baseado no campo risk_level de cada risco
        const risk_counts = risks.reduce((acc, risk) => {
          const level = risk.risk_level;
          if (level) {
            acc[level] = (acc[level] || 0) + 1;
          }
          return acc;
        }, {});
        
        const total_risks = risks.length;
        
        setRiskData({
          // Dados calculados baseado nos riscos
          risk_counts,
          total_risks,
          completed_actions: summaryResponse.data.data.completed_actions || 0,
          expected_today_actions: summaryResponse.data.data.expected_today_actions || 0,
          // Dados completos para a tabela
          risks,
          branchOptions: risksResponse.data.branch_options || [],
          userOptions: risksResponse.data.user_options || [],
        });
        } else {
        // console.warn("APIs retornaram erro, usando dados mock");
        // Fallback para dados mock se as APIs falharem
        const mockRisks = [
          {
            id: 1,
            title: "Risco de Prazo",
            risk_level: "Red",
            owner: "João Silva",
            branch: "TI",
            probability: "High",
            impact: "High",
            hasMitigationPlan: true,
            hasContingencyPlan: false,
            rootCauses: [
              {
                id: "RC1",
                cause: "Falta de recursos",
                actions: [
                  { id: "AC1", type: "mitigation", status: ACTION_STATUS.DONE, title: "Contratar desenvolvedor", dueDate: "2025-01-15" },
                  { id: "AC2", type: "mitigation", status: ACTION_STATUS.IN_PROGRESS, title: "Revisar cronograma", dueDate: "2025-01-20" }
                ]
              }
            ]
          },
          {
            id: 2,
            title: "Risco de Qualidade",
            risk_level: "Yellow",
            owner: "Maria Santos",
            branch: "Qualidade",
            probability: "Medium",
            impact: "High",
            hasMitigationPlan: true,
            hasContingencyPlan: true,
            rootCauses: [
              {
                id: "RC2",
                cause: "Processos inadequados",
                actions: [
                  { id: "AC3", type: "mitigation", status: ACTION_STATUS.DONE, title: "Implementar QA", dueDate: "2025-01-10" },
                  { id: "AC4", type: "contingency", status: ACTION_STATUS.ON_HOLD, title: "Plano de contingência", dueDate: "2025-01-25" }
                ]
              }
            ]
          }
        ];
        
        // Calcular contadores baseado no campo risk_level de cada risco
        const risk_counts = mockRisks.reduce((acc, risk) => {
          const level = risk.risk_level;
          if (level) {
            acc[level] = (acc[level] || 0) + 1;
          }
          return acc;
        }, {});
        
        const mockData = {
          risk_counts,
          total_risks: mockRisks.length,
          completed_actions: 15,
          expected_today_actions: 8,
          risks: mockRisks,
          branchOptions: [
            { id: 1, name: "TI" },
            { id: 2, name: "Qualidade" },
            { id: 3, name: "Operações" }
          ],
          userOptions: [
            { id: 1, name: "João Silva" },
            { id: 2, name: "Maria Santos" },
            { id: 3, name: "Pedro Costa" }
          ]
        };
        setRiskData(mockData);
        }
      } catch (err) {
      // console.error("Erro ao buscar dados dos riscos:", err);
      // console.warn("Usando dados mock devido ao erro");
      // Fallback para dados mock em caso de erro
      const mockRisks = [
        {
          id: 1,
          title: "Risco de Prazo",
          risk_level: "Red",
          owner: "João Silva",
          branch: "TI",
          probability: "High",
          impact: "High",
          hasMitigationPlan: true,
          hasContingencyPlan: false,
          rootCauses: [
            {
              id: "RC1",
              cause: "Falta de recursos",
              actions: [
                { id: "AC1", type: "mitigation", status: ACTION_STATUS.DONE, title: "Contratar desenvolvedor", dueDate: "2025-01-15" },
                { id: "AC2", type: "mitigation", status: ACTION_STATUS.IN_PROGRESS, title: "Revisar cronograma", dueDate: "2025-01-20" }
              ]
            }
          ]
        },
        {
          id: 2,
          title: "Risco de Qualidade",
          risk_level: "Yellow",
          owner: "Maria Santos",
          branch: "Qualidade",
          probability: "Medium",
          impact: "High",
          hasMitigationPlan: true,
          hasContingencyPlan: true,
          rootCauses: [
            {
              id: "RC2",
              cause: "Processos inadequados",
              actions: [
                { id: "AC3", type: "mitigation", status: ACTION_STATUS.DONE, title: "Implementar QA", dueDate: "2025-01-10" },
                { id: "AC4", type: "contingency", status: ACTION_STATUS.ON_HOLD, title: "Plano de contingência", dueDate: "2025-01-25" }
              ]
            }
          ]
        }
      ];
      
      // Calcular contadores baseado no campo risk_level de cada risco
      const risk_counts = mockRisks.reduce((acc, risk) => {
        const level = risk.risk_level;
        if (level) {
          acc[level] = (acc[level] || 0) + 1;
        }
        return acc;
      }, {});
      
      const mockData = {
        risk_counts,
        total_risks: mockRisks.length,
        completed_actions: 15,
        expected_today_actions: 8,
        risks: mockRisks,
        branchOptions: [
          { id: 1, name: "TI" },
          { id: 2, name: "Qualidade" },
          { id: 3, name: "Operações" }
        ],
        userOptions: [
          { id: 1, name: "João Silva" },
          { id: 2, name: "Maria Santos" },
          { id: 3, name: "Pedro Costa" }
        ]
      };
      setRiskData(mockData);
      } finally {
        setIsLoading(false);
      }
  }, []);

  useEffect(() => {
    fetchRiskData();
  }, [fetchRiskData]);

  // Renderizar loading
  if (shouldShowLoading) {
    return (
      <div className={classes.root}>
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}>
          <ThreeDots color={clientColor} height={80} width={80} ariaLabel="loading" />
        </div>
      </div>
    );
  }

  // Se não há dados, mostrar mensagem
  if (!riskData) {
    return (
      <div className={classes.root}>
        <div className={classes.noDataMessage}>
          {t("riskmanagement.no_risks_registered")}
        </div>
      </div>
    );
  }

  // Configuração de cores para cada nível de risco
  // Usa as mesmas cores da matriz de riscos do RiskManagement.js
  // Ordem fixa: ALTO (Red) -> MÉDIO (Yellow) -> BAIXO (Green)
  const riskLevelConfig = {
    Red: { color: "#E57373", label: t("riskmanagement.risk_levels.high") },      // Mesma cor da matriz
    Yellow: { color: "#FFB74D", label: t("riskmanagement.risk_levels.medium") },  // Mesma cor da matriz
    Green: { color: "#81C784", label: t("riskmanagement.risk_levels.low") },   // Mesma cor da matriz
  };

  // Verificar se há dados para exibir
  const hasData = riskData.total_risks > 0;

  if (!hasData) {
    return (
      <div className={classes.root}>
        <div className={classes.noDataMessage}>
          {t("riskmanagement.no_risks_registered")}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Layout de duas colunas: Contadores de Risco + Ações por Tipo */}
      <div className={classes.twoColumnLayout}>
        {/* Coluna Esquerda: Contadores por nível de risco */}
        <div className={classes.leftColumn}>
          <Typography className={classes.sectionTitle}>
            {t("risks.by_level")}
          </Typography>
          <div className={classes.verticalRiskCounters}>
            {/* Sempre mostrar os três níveis na ordem: ALTO, MÉDIO, BAIXO */}
            {["Red", "Yellow", "Green"].map((level) => {
              const count = riskData.risk_counts[level] || 0;
              const config = riskLevelConfig[level];
              return (
                <RiskStatusChip
                  key={level}
                  level={config.label}
                  count={count}
                  total={riskData.total_risks}
                  color={config.color}
                  t={t}
                />
              );
            })}
          </div>
        </div>

        {/* Coluna Direita: Ações por Tipo */}
        <div className={classes.rightColumn}>
          <Typography className={classes.sectionTitle}>
            {t("riskmanagement.actions_by_type")}
          </Typography>
          <div className={classes.verticalActionCards}>
            <ActionStatCard
              icon={faShieldAlt}
              title={t("riskmanagement.mitigation_actions")}
              value={(() => {
                let totalMitigation = 0;
                let completedMitigation = 0;
                let expectedTodayMitigation = 0;
                const today = new Date().toISOString().split('T')[0];
                
                
                riskData.risks.forEach(risk => {
                  // Só conta se o risco tem plano de mitigação ativo
                  if (risk.hasMitigationPlan && risk.rootCauses) {

                    risk.rootCauses.forEach(rc => {
                      if (rc.actions) {
                        const mitigationActions = rc.actions.filter(act => act.type === 'mitigation');

                        
                        totalMitigation += mitigationActions.length;
                        // Usar "Done" como no RiskManagement.js
                        const doneActions = mitigationActions.filter(act => act.status === ACTION_STATUS.DONE);
                        completedMitigation += doneActions.length;

                        
                        // Ações previstas até hoje (não concluídas)
                        const expectedActions = mitigationActions.filter(act => 
                          act.dueDate && act.dueDate <= today && act.status !== ACTION_STATUS.DONE
                        );
                        expectedTodayMitigation += expectedActions.length;

                      }
                    });
                  }
                });
                

                return `${completedMitigation}/${expectedTodayMitigation}`;
              })()}
              color="#2196f3"
              subtitle={t("riskmanagement.completed_expected_today")}
            />
            <ActionStatCard
              icon={faExclamationTriangle}
              title={t("riskmanagement.contingency_actions")}
              value={(() => {
                let totalContingency = 0;
                let completedContingency = 0;
                let expectedTodayContingency = 0;
                const today = new Date().toISOString().split('T')[0];
                
                // console.log("=== Cálculo Ações de Contingência ===");
                // console.log("Data de hoje:", today);
                
                riskData.risks.forEach(risk => {
                  // Só conta se o risco tem plano de contingência ativo
                  if (risk.hasContingencyPlan && risk.rootCauses) {
                    // console.log(`Risco "${risk.title}" tem plano de contingência`);
                    risk.rootCauses.forEach(rc => {
                      if (rc.actions) {
                        const contingencyActions = rc.actions.filter(act => act.type === 'contingency');
                        // console.log(`Root Cause "${rc.cause}": ${contingencyActions.length} ações de contingência`);
                        
                        totalContingency += contingencyActions.length;
                        // Usar "Done" como no RiskManagement.js
                        const doneActions = contingencyActions.filter(act => act.status === ACTION_STATUS.DONE);
                        completedContingency += doneActions.length;
                        // console.log(`Ações concluídas: ${doneActions.length}`);
                        
                        // Ações previstas até hoje (não concluídas)
                        const expectedActions = contingencyActions.filter(act => 
                          act.dueDate && act.dueDate <= today && act.status !== ACTION_STATUS.DONE
                        );
                        expectedTodayContingency += expectedActions.length;
                        // console.log(`Ações previstas até hoje: ${expectedActions.length}`);
                      }
                    });
                  } else {
                    // console.log(`Risco "${risk.title}" NÃO tem plano de contingência`);
                  }
                });
                
                // console.log(`Total: ${totalContingency}, Concluídas: ${completedContingency}, Previstas Hoje: ${expectedTodayContingency}`);
                return `${completedContingency}/${expectedTodayContingency}`;
              })()}
              color="#ff9800"
              subtitle={t("riskmanagement.completed_expected_today")}
            />
          </div>
        </div>
      </div>

      {/* Modal de Detalhes */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            maxHeight: "90vh",
            width: "95vw",
          },
        }}
      >
        <div className={classes.modalTitle}>
          <Typography variant="h6" style={{ color: clientColor, fontWeight: "600" }}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              style={{ marginRight: "8px", color: clientColor }}
            />
            {t("riskmanagement.risk_management")}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            className={classes.closeButton}
            size="small"
          >
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </div>

        <DialogContent className={classes.modalContent} style={{ padding: 0, overflow: "hidden" }}>
          {riskData ? (
            <RiskTable
              risks={riskData.risks || []}
              branchOptions={riskData.branchOptions || []}
              userOptions={riskData.userOptions || []}
              clientColor={clientColor}
              t={t}
            />
          ) : (
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              flexDirection: "column",
              gap: "16px"
            }}>
              <ThreeDots color={clientColor} height={80} width={80} ariaLabel="loading" />
              <Typography variant="body2" style={{ color: "#666", fontSize: "0.8rem" }}>
                {t("riskmanagement.loading_risk_data")}
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RiskSummary;

